import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Paper,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Link as LinkIcon,
  Business,
  AutoAwesome,
  SmartToy,
  VideoCall,
} from '@mui/icons-material';
import ExtractionConfigFormSection from './ExtractionConfigFormSection';
import { formatDate } from '../utils/dateUtils';
import { isExtractionConfigValid } from '../utils/extractionTaxonomy';

/**
 * Domains from the session that also appear on the selected/suggested customer record.
 *
 * @param {object} session - Session row with `source_metadata`.
 * @param {object|null} customer - Customer with optional `domain` array.
 * @returns {string[]} Intersection of external attendee domains and customer domains.
 */
function getMatchingDomainsForCustomer(session, customer) {
  const external = session?.source_metadata?.external_domains || [];
  const cust = customer?.domain;
  if (!Array.isArray(cust) || cust.length === 0) {
    return [];
  }
  const normalizedCust = new Set(
    cust.map((d) => String(d).toLowerCase().trim()).filter(Boolean)
  );
  return external.filter((d) => normalizedCust.has(String(d).toLowerCase().trim()));
}

/**
 * Premium modal to link an unlinked session to a customer and project, with smart match context.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.open - Dialog visibility.
 * @param {function(): void} props.onClose - Close without saving.
 * @param {object|null} props.sessionToLink - Active session row or null.
 * @param {string} props.linkError - Validation or API error message.
 * @param {Array<{ id: string, name: string, domain?: string[] }>} props.allCustomers - Account customers.
 * @param {Array<{ id: string, name: string }>} props.customerProjects - Projects for selected customer.
 * @param {string} props.linkCustomerId - Selected customer id.
 * @param {function(string): void} props.onCustomerChange - Customer select handler.
 * @param {string} props.linkProjectId - Selected project id.
 * @param {function(string): void} props.onProjectChange - Project select handler.
 * @param {object} props.linkExtractionConfig - Extraction config value for Recall sessions.
 * @param {function(object): void} props.onExtractionConfigChange - Extraction config setter.
 * @param {function(object): object|null} props.getSuggestedCustomer - Domain-based suggestion.
 * @param {function(): void} props.onSubmit - Persist link.
 * @returns {JSX.Element} Dialog.
 */
export default function HomeLinkSessionDialog({
  open,
  onClose,
  sessionToLink,
  linkError,
  allCustomers,
  customerProjects,
  linkCustomerId,
  onCustomerChange,
  linkProjectId,
  onProjectChange,
  linkExtractionConfig,
  onExtractionConfigChange,
  getSuggestedCustomer,
  onSubmit,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const suggested = sessionToLink ? getSuggestedCustomer(sessionToLink) : null;
  const selectedCustomer = useMemo(
    () => allCustomers.find((c) => c.id === linkCustomerId) || null,
    [allCustomers, linkCustomerId]
  );

  /** Customers sorted alphabetically by name (case-insensitive), matching /customers and the Generate wizard. */
  const sortedCustomers = useMemo(
    () =>
      [...allCustomers].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      ),
    [allCustomers]
  );

  const matchingDomains = useMemo(() => {
    if (!sessionToLink || !selectedCustomer) {
      return [];
    }
    return getMatchingDomainsForCustomer(sessionToLink, selectedCustomer);
  }, [sessionToLink, selectedCustomer]);

  const needsExtraction =
    sessionToLink?.source === 'recall' && !sessionToLink?.extraction_type;
  const extractionOk = !needsExtraction || isExtractionConfigValid(linkExtractionConfig);
  const canSubmit = !!linkProjectId && extractionOk;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} disableRestoreFocus>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon sx={{ color: 'var(--q-accent)' }} />
          <span>Link session to a project</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {sessionToLink && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: 'var(--q-bg-subtle)',
              borderColor: 'divider',
              borderRadius: 'var(--q-radius-xl)',
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                {sessionToLink.name}
              </Typography>
              <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(sessionToLink.created_at)}
                </Typography>
                <Chip
                  size="small"
                  icon={
                    sessionToLink.source === 'recall' ? (
                      <SmartToy sx={{ fontSize: 14 }} />
                    ) : (
                      <VideoCall sx={{ fontSize: 14 }} />
                    )
                  }
                  label={sessionToLink.source === 'recall' ? 'Quill bot' : 'Fathom'}
                />
              </Stack>
              {(sessionToLink.source_metadata?.external_domains || []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Attendee domains detected
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {(sessionToLink.source_metadata?.external_domains || []).map((d) => (
                      <Chip key={d} label={d} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        {linkError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {linkError}
          </Alert>
        )}

        {sessionToLink && suggested && linkCustomerId === suggested.id && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 'var(--q-radius-xl)',
              border: '1px solid var(--q-accent-border)',
              bgcolor: 'var(--q-accent-tint)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <AutoAwesome sx={{ mt: 0.25, color: 'var(--q-accent)' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'var(--q-accent-strong)' }}>
                  Suggested customer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  We matched attendee domains to <strong>{suggested.name}</strong>. You can keep this pick or choose
                  another customer below.
                </Typography>
                {matchingDomains.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Matching domains:
                    </Typography>
                    {matchingDomains.map((d) => (
                      <Chip key={d} label={d} size="small" color="primary" />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {sessionToLink && suggested && linkCustomerId && linkCustomerId !== suggested.id && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<Business />}>
            You switched away from the suggested customer <strong>{suggested.name}</strong>. The selection below will be
            used when linking.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="home-link-customer-label">Customer</InputLabel>
          <Select
            labelId="home-link-customer-label"
            value={linkCustomerId}
            onChange={(e) => onCustomerChange(e.target.value)}
            label="Customer"
          >
            {sortedCustomers.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <span>{c.name}</span>
                  {suggested && c.id === suggested.id && (
                    <Chip label="Suggested" size="small" color="primary" />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!linkCustomerId}>
          <InputLabel id="home-link-project-label">Project</InputLabel>
          <Select
            labelId="home-link-project-label"
            value={linkProjectId}
            onChange={(e) => onProjectChange(e.target.value)}
            label="Project"
          >
            {customerProjects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
            {customerProjects.length === 0 && linkCustomerId && (
              <MenuItem disabled>No projects for this customer</MenuItem>
            )}
          </Select>
        </FormControl>

        {needsExtraction && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Extraction settings <Typography component="span" color="error">*</Typography>
            </Typography>
            <ExtractionConfigFormSection value={linkExtractionConfig} onChange={onExtractionConfigChange} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={<LinkIcon />}
        >
          Link session
        </Button>
      </DialogActions>
    </Dialog>
  );
}
